import ZoomInIcon from "@mui/icons-material/ZoomIn";

import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Box, IconButton, Stack, SxProps, Theme } from "@mui/material";

//@ts-ignore
import BpmnModeler from "bpmn-js/lib/Modeler";
import { useSelectedOrganization } from "hooks";
import { bpmnDiagramThemeSx } from "utils/bpmnDiagramStyles";

interface DropZoneElementTypes {
  shouldShowDropZoneError: boolean;
  onClickEditorCollapse: () => void;
  shouldShowFlowNodeEditor: boolean;
  bpmnModeler: BpmnModeler;
  dropZoneRef: any;
}

const getModelerCanvasSx = (isAdmin?: boolean): SxProps<Theme> => ({
  ...bpmnDiagramThemeSx,
  "& .bjs-drilldown": { display: "none" },
  "& .djs-minimap": { display: isAdmin ? "block" : "none" },
  "& .djs-palette": { display: isAdmin ? "block" : "none" },
  "& .djs-palette-open": {
    width: "calc(100% - 118px)!important",
  },
  "& .djs-direct-editing-content": {
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? "#000000 !important" : theme.palette.background.paper,
    color: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.text.primary} !important` : theme.palette.text.primary,
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.primary.main} !important` : theme.palette.divider,
    boxShadow: (theme) =>
      theme.palette.mode === "dark" ? "none !important" : undefined,
    outline: "none !important",
    caretColor: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.primary.main} !important` : undefined,
  },
  "& .djs-direct-editing-content *, & .djs-direct-editing-parent": {
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? "transparent !important" : undefined,
    color: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.text.primary} !important` : undefined,
  },
  "& .djs-direct-editing-resize-handle": {
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.primary.main} !important` : undefined,
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.primary.main} !important` : undefined,
  },
  "& .djs-palette, & .djs-popup": {
    bgcolor: "background.paper",
    color: "text.primary",
    borderColor: "divider",
    boxShadow: (theme) =>
      theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.45)" : undefined,
  },
  "& .djs-context-pad.open": {
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? "#000000" : theme.palette.background.paper,
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.10)" : theme.palette.divider,
    borderRadius: "6px",
    p: 0.5,
    boxShadow: (theme) =>
      theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.45)" : undefined,
  },
  "& .djs-context-pad.open .entry": {
    width: 22,
    height: 22,
    lineHeight: "22px",
    borderRadius: "4px",
    margin: "1px",
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? "transparent !important" : undefined,
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? "transparent !important" : undefined,
    outline: (theme) =>
      theme.palette.mode === "dark" ? "none !important" : undefined,
    boxShadow: (theme) =>
      theme.palette.mode === "dark" ? "none !important" : undefined,
    color: (theme) =>
      theme.palette.mode === "dark"
        ? "#ffffff !important"
        : theme.palette.secondary.main,
    transition: "background-color 0.15s ease, color 0.15s ease",
  },
  "& .djs-context-pad.open .entry:before": {
    color: "inherit !important",
    lineHeight: "22px",
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? "transparent !important" : undefined,
    outline: (theme) =>
      theme.palette.mode === "dark" ? "none !important" : undefined,
    boxShadow: (theme) =>
      theme.palette.mode === "dark" ? "none !important" : undefined,
  },
  "& .djs-context-pad.open .entry:hover, & .djs-context-pad.open .entry.active": {
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(84, 189, 193, 0.22) !important" : undefined,
    color: (theme) =>
      theme.palette.mode === "dark"
        ? `${theme.palette.primary.main} !important`
        : theme.palette.primary.main,
  },
  "& .djs-context-pad.open .entry.disabled": {
    color: "text.disabled",
    opacity: 0.55,
  },
  "& .djs-context-pad.open .entry.remove": {
    color: (theme) =>
      theme.palette.mode === "dark"
        ? `${theme.palette.primary.main} !important`
        : theme.palette.secondary.main,
  },
  "& .djs-palette .entry, & .djs-popup .entry": {
    color: "text.primary",
    borderColor: "divider",
  },
  "& .djs-palette .entry:hover, & .djs-popup .entry:hover": {
    bgcolor: "action.hover",
  },
  "& .djs-popup-header": {
    bgcolor: "background.paper",
    borderColor: "divider",
  },
  "& .djs-minimap, & .djs-minimap .map": {
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? "#000000" : theme.palette.background.paper,
    borderColor: "divider",
  },
  "& .djs-minimap .viewport-dom": {
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? theme.palette.primary.main : "orange",
  },
  "& .djs-minimap.open .overlay": {
    background: (theme) =>
      theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.16) !important" : undefined,
  },
  "& .djs-minimap .djs-visual > rect, & .djs-minimap .djs-visual > circle, & .djs-minimap .djs-visual > ellipse, & .djs-minimap .djs-visual > polygon": {
    fill: (theme) =>
      theme.palette.mode === "dark" ? "#000000 !important" : undefined,
    stroke: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.grey[300]} !important` : undefined,
  },
  "& .djs-minimap .djs-visual path, & .djs-minimap .djs-visual line, & .djs-minimap .djs-visual polyline": {
    stroke: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.grey[300]} !important` : undefined,
  },
  "& .djs-minimap .djs-visual path[fill='black'], & .djs-minimap .djs-visual path[fill='#000'], & .djs-minimap .djs-visual path[fill='#000000']": {
    fill: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.grey[300]} !important` : undefined,
  },
  "& .djs-minimap text, & .djs-minimap tspan": {
    fill: (theme) =>
      theme.palette.mode === "dark" ? `${theme.palette.text.primary} !important` : undefined,
  },
});

const DropZoneElement: React.FC<DropZoneElementTypes> = ({
  shouldShowDropZoneError,
  bpmnModeler,
  dropZoneRef,
}) => {
  const selectedOrganization = useSelectedOrganization();
  const isAdmin = selectedOrganization?.administrator;
  return (
    <div
      className={`content ${shouldShowDropZoneError ? "with-error" : "with-diagram"
        }`}
      id="js-drop-zone"
      ref={dropZoneRef}
    >
      {isAdmin && (
        <Stack
          sx={{ position: "absolute", top: 190, left: 20, zIndex: 10 }}
          direction="row"
          spacing={1}
        >
          <IconButton
            onClick={() => {
              bpmnModeler.get<any>("zoomScroll").zoom(1);
            }}
          >
            <ZoomInIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              bpmnModeler.get<any>("zoomScroll").zoom(-1);
            }}
          >
            <ZoomOutIcon />
          </IconButton>
        </Stack>
      )}
      <Box
        sx={getModelerCanvasSx(isAdmin)}
        className="canvas" id="js-canvas"></Box>
    </div>
  );
};

export default DropZoneElement;
